import { useCallback, useEffect, useReducer, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"

import { estimatesApi } from "@/lib/api/estimates"
import { queryKeys } from "@/lib/api/query-keys"
import type {
  ApiEstimate,
  ApiEstimateLineItem,
  PhotoLink,
  UpdateEstimateLineItemRowDto,
  VehicleComponent,
  VehicleSide,
} from "@/lib/api/types"
import { COMPONENT_OPTIONS, type SideBucket, sideBucket } from "@/lib/estimates/catalog"
import { lineKey, photoLinksOf } from "@/lib/estimates/photo-links"
import { runSectionsOrchestratorAndWait } from "@/components/estimates/full-estimate/submit"

// The working copy of the estimate while editing. The estimate is the one
// list; each line knows which photos show it. One rule holds everywhere:
// one component on one side is one line — adding it again ticks it for
// the photo you are on, it never duplicates.

export type LineAction = "REPAIR" | "REPLACE"
export type LinePartType = "OEM" | "Aftermarket"

export interface CanvasLine {
  /** Local identity for React — not the dedupe key. */
  key: string
  id: number | null
  component: string
  section: string
  side: SideBucket
  action: LineAction
  partType: LinePartType
  severity: number
  description: string
  photos: PhotoLink[]
  /** Found by the damage agent, or added by a person. */
  addedBy: "ai" | "user"
  /** The line's own estimate (the BE's avg); null until priced. */
  avg: number | null
  manHours: number
}

export interface ComponentPick {
  component: string
  side: SideBucket
}

interface State {
  lines: CanvasLine[]
  dirty: boolean
  nextKey: number
  notice: { id: number; text: string } | null
}

type Msg =
  | { t: "reset"; estimate: ApiEstimate }
  | { t: "toggle"; key: string; imageId: number }
  | { t: "add"; pick: ComponentPick; imageId: number | null }
  | { t: "update"; key: string; patch: Partial<Pick<CanvasLine, "action" | "partType" | "side">> }
  | { t: "remove"; key: string }
  | { t: "restore"; line: CanvasLine }

function sectionOf(component: string, fallback: string): string {
  return COMPONENT_OPTIONS.find((o) => o.value === component)?.section ?? fallback
}

function toLine(li: ApiEstimateLineItem): CanvasLine {
  return {
    key: `srv-${li.id}`,
    id: li.id,
    component: li.component,
    section: li.section ?? sectionOf(li.component, "MISCELLANEOUS"),
    side: sideBucket(li.side),
    action: li.action?.toUpperCase() === "REPLACE" ? "REPLACE" : "REPAIR",
    partType: (li.partType ?? "").toLowerCase() === "aftermarket" ? "Aftermarket" : "OEM",
    severity: li.severity ?? 5,
    description: li.description ?? "",
    photos: photoLinksOf(li),
    addedBy: li.metadata?.addedBy === "user" ? "user" : "ai",
    avg: li.avg ?? null,
    manHours: li.manHours ?? 0,
  }
}

function fromEstimate(estimate: ApiEstimate, prev?: State): State {
  const items = (estimate.lineItems ?? []).filter((li) => li.isActive !== false)
  return { lines: items.map(toLine), dirty: false, nextKey: prev?.nextKey ?? 1, notice: null }
}

const link = (imageId: number): PhotoLink => ({ imageId, x: null, y: null, by: "user", confirmed: true })

function findByIdentity(lines: CanvasLine[], component: string, side: string) {
  const k = lineKey(component, side)
  return lines.find((l) => lineKey(l.component, l.side) === k)
}

function reducer(s: State, m: Msg): State {
  const notice = (text: string) => ({ id: (s.notice?.id ?? 0) + 1, text })
  const patchLine = (key: string, fn: (l: CanvasLine) => CanvasLine) => ({
    ...s,
    dirty: true,
    lines: s.lines.map((l) => (l.key === key ? fn(l) : l)),
  })

  switch (m.t) {
    case "reset":
      return fromEstimate(m.estimate, s)

    case "toggle":
      return patchLine(m.key, (l) =>
        l.photos.some((p) => p.imageId === m.imageId)
          ? { ...l, photos: l.photos.filter((p) => p.imageId !== m.imageId) }
          : { ...l, photos: [...l.photos, link(m.imageId)] },
      )

    case "add": {
      const existing = findByIdentity(s.lines, m.pick.component, m.pick.side)
      if (existing) {
        if (m.imageId == null) return { ...s, notice: notice("Already on the estimate") }
        if (existing.photos.some((p) => p.imageId === m.imageId)) {
          return { ...s, notice: notice("Already on the estimate and ticked for this photo") }
        }
        return {
          ...patchLine(existing.key, (l) => ({ ...l, photos: [...l.photos, link(m.imageId as number)] })),
          notice: notice("Already on the estimate — ticked for this photo"),
        }
      }
      const line: CanvasLine = {
        key: `new-${s.nextKey}`,
        id: null,
        component: m.pick.component,
        section: sectionOf(m.pick.component, "MISCELLANEOUS"),
        side: m.pick.side,
        action: "REPAIR",
        partType: "OEM",
        severity: 5,
        description: "",
        photos: m.imageId == null ? [] : [link(m.imageId)],
        addedBy: "user",
        avg: null,
        manHours: 0,
      }
      return { ...s, lines: [...s.lines, line], dirty: true, nextKey: s.nextKey + 1, notice: null }
    }

    case "update": {
      const line = s.lines.find((l) => l.key === m.key)
      if (!line) return s
      const side = m.patch.side ?? line.side
      if (side !== line.side) {
        const other = findByIdentity(s.lines.filter((l) => l.key !== m.key), line.component, side)
        if (other) {
          const seen = new Set(other.photos.map((p) => p.imageId))
          const merged = [...other.photos, ...line.photos.filter((p) => !seen.has(p.imageId))]
          return {
            ...s,
            dirty: true,
            lines: s.lines.filter((l) => l.key !== m.key).map((l) => (l.key === other.key ? { ...l, photos: merged } : l)),
            notice: notice("Merged with the same part already on that side"),
          }
        }
      }
      return patchLine(m.key, (l) => ({ ...l, ...m.patch }))
    }

    case "remove":
      return { ...s, dirty: true, lines: s.lines.filter((l) => l.key !== m.key) }

    case "restore":
      if (s.lines.some((l) => l.key === m.line.key)) return s
      return { ...s, dirty: true, lines: [...s.lines, m.line] }
  }
}

export function useCanvas(estimate: ApiEstimate) {
  const qc = useQueryClient()
  const [state, dispatch] = useReducer(reducer, estimate, (e) => fromEstimate(e))
  const [saving, setSaving] = useState(false)

  // A fresh estimate from the server replaces the working copy — never
  // while the advisor is mid-edit.
  useEffect(() => {
    if (!state.dirty && !saving) dispatch({ t: "reset", estimate })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimate])

  const save = useCallback(async () => {
    setSaving(true)
    try {
      const rows: UpdateEstimateLineItemRowDto[] = state.lines.map((l) => ({
        id: l.id ?? undefined,
        component: l.component as VehicleComponent,
        description: l.description || undefined,
        action: l.action,
        side: l.side as VehicleSide,
        partType: l.partType,
        severity: l.severity,
        photos: l.photos,
        addedBy: l.addedBy,
      }))
      await estimatesApi.updateLineItems(estimate.id, { lineItems: rows })
      await runSectionsOrchestratorAndWait(estimate.id)
      const fresh = await estimatesApi.get(estimate.id)
      dispatch({ t: "reset", estimate: fresh })
      qc.setQueryData(queryKeys.estimates.detail(estimate.id), fresh)
      await qc.invalidateQueries({ queryKey: queryKeys.estimates.all })
      await qc.invalidateQueries({ queryKey: queryKeys.tasks.all })
    } finally {
      setSaving(false)
    }
  }, [estimate.id, qc, state.lines])

  return { ...state, saving, dispatch, save }
}

export type CanvasDispatch = React.Dispatch<Msg>
